/**
 * PropertyInformationPanel — native rewrite of the legacy PropertyInformation
 * sub-tab. Lifts the design-system JSX; wires inputs to the form controller.
 */

import React from 'react'
import { Box, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'

import { BuildingLocationType, BuildingType } from 'constants/purchaseFlow'
import { GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUIInputFieldCounter } from 'components/common/MUIInputFieldCounter'
import { MUITooltip } from 'components/common/MUITooltip'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'

import type { UseEnergyCertificateForm } from '../useEnergyCertificateForm'
import { NumericFloatField } from 'components/common/MUIFloatNumberField'

const InputMobileWidth = { xs: '100%', sm: '40rem', lg: '50rem' }

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

export const PropertyInformationPanel: React.FC<{ form: UseEnergyCertificateForm }> = ({ form }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'energy_certificate_step.property_information_page' })
  const { view, setBuildingType, setBuildingLocation, setResidentialUnit, setConstructionYear, setLivingSpace, setCommercialArea, setCommercialAreaType } = form

  return (
    <SectionedBorderBox title={t('title')}>
      <Stack gap="1.8rem" width={InputMobileWidth} position="relative">

        {/* BUILDING TYPE */}
        <MUIDropdown
          sx={{ width: '100%' }}
          button={(_isOpen, action) => (
            <MUIDropdownInput
              readOnly
              required
              label={t('property_type')}
              type={DropdownInputTypes.SELECTOR}
              value={t(view.buildingType ? `building_type.${view.buildingType}` : 'select_building_type_option')}
              onClick={action}
            />
          )}
        >
          {Object.values(BuildingType).map((type) => (
            <MUIDropdownItem key={type} onClick={() => setBuildingType(type)}>
              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`building_type.${type}`)}</Typography>
            </MUIDropdownItem>
          ))}
        </MUIDropdown>

        {/* BUILDING LOCATION */}
        <MUIDropdown
          sx={{ width: '100%' }}
          button={(_isOpen, action) => (
            <MUIDropdownInput
              readOnly
              required
              label={t('building_location')}
              type={DropdownInputTypes.SELECTOR}
              value={t(view.buildingLocationType ? `building_location_type.${view.buildingLocationType}` : 'select_building_location_option')}
              onClick={action}
            />
          )}
        >
          {Object.values(BuildingLocationType).map((type) => (
            <MUIDropdownItem key={type} onClick={() => setBuildingLocation(type)}>
              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`building_location_type.${type}`)}</Typography>
            </MUIDropdownItem>
          ))}
        </MUIDropdown>

        {/* RESIDENTIAL UNITS */}
        <MUIInputFieldCounter
          minValue={1}
          counterValue={view.residentialUnit}
          counterOnChange={setResidentialUnit}
          inputBoxWidth="14rem"
          label={t('residential_units')}
        />

        {/* COMMERCIAL FIELDS — only for MIXED_USE_BUILDING */}
        {view.buildingType === BuildingType.MIXED_USE_BUILDING && <>

          <Box width={{ xs: '100%', sm: '25rem' }}>
            <MUIInputField
              required
              label={t('commercial_area')}
              value={view.commercialArea}
              onChange={(e) => setCommercialArea(onlyDigits(e.target.value))}
            />
          </Box>

          <Box width={{ xs: '100%', sm: '25rem' }}>
            <MUIInputField
              required
              label={t('commercial_area_type')}
              value={view.commercialAreaType}
              onChange={(e) => setCommercialAreaType(e.target.value)}
            />
          </Box>

        </>}

        {/* CONSTRUCTION YEAR */}
        <Box width={{ xs: '100%', sm: '25rem' }}>
          <MUIInputField
            required
            label={t('construction_year')}
            value={view.constructionYear}
            onChange={(e) => setConstructionYear(onlyDigits(e.target.value))}
          />
        </Box>

        {/* LIVING SPACE */}
        <Box width={{ xs: '100%', sm: '25rem' }}>
          <NumericFloatField
            required
            label={t('living_space')}
            suffix="m2"
            tooltip={
              <MUITooltip content={t('living_area_tooltip')}>
                <InfoOutlinedIcon fontSize="medium" sx={{ paddingTop: '.4rem' }} />
              </MUITooltip>
            }
            value={view.livingSpace}
            onCommit={setLivingSpace}
          />
        </Box>

      </Stack>
    </SectionedBorderBox>
  )
}
